import { Injectable, inject } from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {forkJoin, map} from "rxjs";
import {environment} from "../environments/environment.development";
import {controllersNames} from "../app/controllersNames";
import {Course} from "../app/interfaces/courses/Course";
import {CourseParentCategory} from "../app/interfaces/courses/CourseParentCategory";
import {GetCourseDto} from "../app/dto/courses/GetCourseDto";
import {MyCourseProgress} from "../app/interfaces/courses/MyCourseProgress";
import {LikeType} from "../app/enums/news/LikeType";

@Injectable({
    providedIn: 'root'
})
export class CoursesService {
    private http = inject(HttpClient);


    controller = controllersNames.courses;
    api = environment.apiBase + '/' + this.controller;
    limit = 30;

    // allow anonymous — пустой ответ (404) при выключенном разделе
    getCourseList(page: number = 1, categorySlug?: string, tagSlug?: string,
                  withDrafts?: boolean, sort?: 'new' | 'top',
                  limit?: number) {
        const pageSize = limit ?? this.limit;
        let params = new HttpParams();
        let offset = (page - 1) * pageSize;

        params = params.append('limit', pageSize.toString());
        if (offset != 0) {
            params = params.append('offset', offset.toString());
        }
        if (categorySlug) {
            params = params.append('categorySlug', categorySlug);
        }
        if (tagSlug) {
            params = params.append('tagSlug', tagSlug);
        }
        if (withDrafts) {
            params = params.append('withDrafts', true);
        }
        if (sort) {
            params = params.append('sort', sort);
        }

        return this.http.get<Course[]>(this.api, {params, observe: 'response'})
          .pipe(map(res => ({
            items: res.body ?? [],
            total: Number(res.headers.get('X-Total-Count') ?? 0)
          })));
    }

    getSingleBySlug(slug: string) {
        return this.http.get<GetCourseDto>(this.api + '/' + slug);
    }

    /** Отметка «урок пройден» — toggle (повторный вызов снимает отметку). Требует логин. */
    toggleProgress(slug: string, episodeNumber: number) {
        return this.http.post<{videoId: number; completed: boolean}>(
            `${this.api}/${slug}/episodes/${episodeNumber}/progress`, {});
    }

    /** «Где я остановился» — запоминает последний открытый урок (fire-and-forget). */
    recordVisit(slug: string, episodeNumber: number) {
        return this.http.post<void>(
            `${this.api}/${slug}/episodes/${episodeNumber}/visit`, {});
    }

    /** «Мои курсы» в кабинете — прогресс по каждому посещённому курсу. */
    myProgress() {
        return this.http.get<MyCourseProgress[]>(this.api + '/my-progress');
    }

    /**
     * Ссылка на видеофайл эпизода (JWT + premium-check на бэке).
     * {url} — подписанная secure_link либо относительный путь (dev).
     */
    getVideoLink(slug: string, episodeNumber: number) {
        return this.http.get<{ url: string; expires?: number }>(
            `${this.api}/${slug}/episodes/${episodeNumber}/link`);
    }

    getRelated(slug: string, limit: number = 6) {
        const params = new HttpParams().set('limit', limit);
        return this.http.get<Course[]>(`${this.api}/${slug}/related`, {params});
    }

    getCategoriesTree(withCoursesOnly = false) {
        const tree$ = this.http.get<CourseParentCategory[]>(`${this.api}/categories-tree`);
        if (!withCoursesOnly) return tree$;

        // публичный каталог: оставляем только категории с включёнными курсами —
        // кнопки-фильтры по пустым/выключенным категориям бесполезны
        return forkJoin({
            tree: tree$,
            courses: this.getCourseList(1, undefined, undefined, undefined, undefined, 500)
        }).pipe(map(({tree, courses}) => {
            const slugs = new Set(courses.items.map(c => c.category?.slug).filter(Boolean));
            return tree
                .map(pc => ({...pc, categories: pc.categories.filter(c => slugs.has(c.slug))}))
                .filter(pc => pc.categories.length > 0);
        }));
    }

    updateLikeVote(likeType: LikeType, courseId: number) {
        return this.http.post<{
            dislikesCount: number,
            likesCount: number,
            rate: number,
            removeVote: boolean
        }>(`${this.api}/UpdateLike`, {courseId, type: likeType});
    }

    updateFavorites(courseId: number, remove: boolean) {
        return this.http.post<{ status: number }>(
            `${this.api}/${courseId}/UpdateFavorites`, null,
            {params: new HttpParams().set('remove', remove)});
    }

    /** избранные курсы текущего юзера — нужен JWT */
    getFavorites() {
        return this.http.get<Course[]>(`${this.api}/GetFavorites`);
    }
}
